import { Component, OnInit } from "@angular/core";
import { firstValueFrom } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { CipherService } from "@bitwarden/common/vault/abstractions/cipher.service";
import { CipherType } from "@bitwarden/common/vault/enums";
import { CipherView } from "@bitwarden/common/vault/models/view/cipher.view";
import { DialogService } from "@bitwarden/components";

import { HeaderModule } from "../layouts/header/header.module";
import { SharedModule } from "../shared";

type DuplicateRow = {
  cipher: CipherView;
  domain: string;
  username: string;
  selected: boolean;
};

type DuplicateGroup = {
  key: string;
  domain: string;
  username: string;
  rows: DuplicateRow[];
};

@Component({
  selector: "app-duplicate-logins",
  templateUrl: "duplicate-logins.component.html",
  imports: [SharedModule, HeaderModule],
})
export class DuplicateLoginsComponent implements OnInit {
  loading = true;
  deleting = false;
  filter = "";
  groups: DuplicateGroup[] = [];

  constructor(
    private accountService: AccountService,
    private cipherService: CipherService,
    private dialogService: DialogService,
    private platformUtilsService: PlatformUtilsService,
  ) {}

  async ngOnInit() {
    await this.scan();
  }

  async scan() {
    this.loading = true;
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    const ciphers = await this.cipherService.getAllDecrypted(userId);
    const grouped = new Map<string, DuplicateGroup>();

    for (const cipher of ciphers) {
      if (
        cipher.type !== CipherType.Login ||
        cipher.deletedDate != null ||
        cipher.organizationId != null
      ) {
        continue;
      }

      const username = (cipher.login?.username ?? "").trim().toLocaleLowerCase();
      const domains = new Set(
        (cipher.login?.uris ?? [])
          .map((uri) => this.normalizedDomain(uri.uri ?? ""))
          .filter((domain): domain is string => domain.length > 0),
      );

      for (const domain of domains) {
        const key = `${domain}\u0000${username}`;
        const group = grouped.get(key) ?? { key, domain, username, rows: [] };
        group.rows.push({ cipher, domain, username, selected: false });
        grouped.set(key, group);
      }
    }

    this.groups = [...grouped.values()]
      .filter((group) => group.rows.length > 1)
      .sort((a, b) => a.domain.localeCompare(b.domain) || a.username.localeCompare(b.username));
    this.loading = false;
  }

  get visibleGroups() {
    const filter = this.filter.trim().toLocaleLowerCase();
    return filter.length === 0
      ? this.groups
      : this.groups.filter(
          (group) =>
            group.domain.includes(filter) ||
            group.username.includes(filter) ||
            group.rows.some((row) => row.cipher.name.toLocaleLowerCase().includes(filter)),
        );
  }

  get selectedCount() {
    return new Set(
      this.groups.flatMap((group) => group.rows.filter((row) => row.selected).map((row) => row.cipher.id)),
    ).size;
  }

  selectAllButNewest(group: DuplicateGroup) {
    const newest = [...group.rows].sort(
      (a, b) => b.cipher.revisionDate.getTime() - a.cipher.revisionDate.getTime(),
    )[0];
    for (const row of group.rows) {
      row.selected = row !== newest;
    }
  }

  clearSelection() {
    for (const group of this.groups) {
      for (const row of group.rows) row.selected = false;
    }
  }

  async deleteSelected() {
    const ids = [
      ...new Set(
        this.groups.flatMap((group) =>
          group.rows.filter((row) => row.selected).map((row) => row.cipher.id),
        ),
      ),
    ];
    if (ids.length === 0) return;

    const confirmed = await this.dialogService.openSimpleDialog({
      type: "danger",
      title: { key: "deleteItems" },
      content: { key: "deleteItemsConfirmation" },
      acceptButtonText: { key: "delete" },
      cancelButtonText: { key: "cancel" },
    });
    if (!confirmed) return;

    this.deleting = true;
    const userId = await firstValueFrom(this.accountService.activeAccount$.pipe(getUserId));
    for (const id of ids) {
      await this.cipherService.softDeleteWithServer(id, userId);
    }
    this.platformUtilsService.showToast(
      "success",
      null,
      `${ids.length} duplicate login${ids.length === 1 ? "" : "s"} moved to trash.`,
    );
    this.deleting = false;
    await this.scan();
  }

  private normalizedDomain(value: string): string {
    const trimmed = value.trim();
    if (trimmed.length === 0) return "";
    try {
      const parsed = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
      return parsed.hostname.toLocaleLowerCase().replace(/^www\./, "").replace(/\.$/, "");
    } catch {
      return trimmed.toLocaleLowerCase().replace(/^https?:\/\//, "").split(/[/?#]/, 1)[0].replace(/^www\./, "");
    }
  }
}
